import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Step1Info from './steps/Step1Info';
import Step2Docs from './steps/Step2Docs';
import Step3Products from './steps/Step3Products';
import Step4Payment from './steps/Step4Payment';
import { styles } from './styles';
import { UEN_PIX_KEY } from '../../utils';
import studentsBg from '../../assets/patterns/students-bg.svg';
import loginImg from '../../assets/loginImg.jpg';

const TOTAL_STEPS = 4;

export default function RegistrarUser() {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({});
    const [pixData, setPixData] = useState(null);
    // No mobile a ilustração da direita só atrapalha (rouba espaço/scroll),
    // então não renderizamos ela abaixo de 900px.
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth < 900 : false
    );

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 900);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Se houver Pix pendente, salta direto pro pagamento.
    useEffect(() => {
        const savedPix = localStorage.getItem(UEN_PIX_KEY);
        if (savedPix) {
            try {
                setPixData(JSON.parse(savedPix));
                setStep(4);
            } catch (error) {
                console.error("Erro ao carregar dados do cache", error);
                localStorage.removeItem(UEN_PIX_KEY);
            }
        }
    }, []);

    const updateData = (data) => setFormData(prev => ({ ...prev, ...data }));
    const nextStep = () => setStep(prev => prev + 1);
    const prevStep = () => setStep(prev => prev - 1);

    const handleSetPix = (data) => {
        localStorage.setItem(UEN_PIX_KEY, JSON.stringify(data));
        setPixData(data);
        setStep(4);
    };

    const renderStep = () => {
        switch (step) {
            case 1: return <Step1Info next={nextStep} update={updateData} data={formData} />;
            case 2: return <Step2Docs next={nextStep} prev={prevStep} update={updateData} data={formData} />;
            case 3: return <Step3Products prev={prevStep} setPix={handleSetPix} data={formData} />;
            case 4: return <Step4Payment pixData={pixData} />;
            default: return null;
        }
    };

    const titlesByStep = {
        1: 'Crie sua conta',
        2: 'Dados de acesso',
        3: 'Escolha seu produto',
        4: 'Pagamento Pix',
    };
    const subtitlesByStep = {
        1: 'Vamos começar com seus dados pessoais.',
        2: 'Defina seu login e data de nascimento.',
        3: 'Escolha a carteirinha e a forma de pagamento.',
        4: 'Finalize o pagamento para emitir sua carteira.',
    };

    return (
        <div style={styles.pageContainer} className="uen-reg-bg">
            <style>{`
                .uen-reg-bg { position: relative; isolation: isolate; }
                .uen-reg-bg::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background-image: url(${studentsBg});
                    background-repeat: repeat;
                    background-size: 240px 240px;
                    opacity: 0.04;
                    pointer-events: none;
                    z-index: -1;
                }
            `}</style>
            {/* ---------- Formulário (esquerda) ---------- */}
            <div style={styles.registerSection}>
                <div style={styles.formContainer}>
                    {/* Brand */}
                    <div style={brandStyles.brand}>
                        <div style={brandStyles.logo}>U</div>
                        <div style={brandStyles.textCol}>
                            <span style={brandStyles.name}>UEN</span>
                            <span style={brandStyles.tag}>Estudantil</span>
                        </div>
                    </div>

                    {/* Stepper visual */}
                    {step <= TOTAL_STEPS && (
                        <>
                            <div style={styles.stepper}>
                                {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                                    <div key={i} style={styles.stepperBar(i + 1 <= step)} />
                                ))}
                            </div>
                            <p style={{ fontSize: '0.8rem', color: '#94A3B8', marginTop: 0, marginBottom: '1rem' }}>
                                Passo {step} de {TOTAL_STEPS}
                            </p>
                        </>
                    )}

                    <h1 style={styles.title}>{titlesByStep[step]}</h1>
                    <p style={styles.subtitle}>{subtitlesByStep[step]}</p>

                    {renderStep()}

                    {step < 4 && (
                        <div style={{ ...styles.signInRow, marginTop: '1.5rem' }}>
                            Já possui uma conta?
                            <Link to="/login" style={styles.signInLink}>Entrar</Link>
                        </div>
                    )}
                </div>
            </div>

            {/* ---------- Ilustração (direita) — escondida no mobile ---------- */}
            {!isMobile && (
                <div style={{ ...styles.illustrationSection, position: 'relative', overflow: 'hidden' }}>
                    <img
                        src={loginImg}
                        alt=""
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                </div>
            )}
        </div>
    );
}

const brandStyles = {
    brand: { display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' },
    logo: {
        width: 44, height: 44, borderRadius: 12,
        background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)',
        color: '#fff', display: 'grid', placeItems: 'center',
        fontWeight: 800, fontSize: 18,
        boxShadow: '0 4px 12px -2px rgba(5, 150, 105, 0.4)',
    },
    textCol: { display: 'flex', flexDirection: 'column', lineHeight: 1.1 },
    name: { fontWeight: 700, fontSize: '1.1rem', color: '#0F172A' },
    tag: { fontSize: '0.75rem', color: '#64748B', marginTop: 2 },
};
